import { Checkbox } from "@kobalte/core/checkbox";
import {
  type SortingState,
  type ColumnDef,
  createSolidTable,
  getCoreRowModel,
  getSortedRowModel,
  type Header,
  flexRender,
} from "@tanstack/solid-table";
import {
  TbCheck,
  TbLoader2,
  TbAlertTriangle,
  TbFileText,
  TbCircleCheckFilled,
  TbCaretUp,
  TbCaretDown,
} from "solid-icons/tb";
import { createSignal, Switch, Match, For, Show, createMemo } from "solid-js";
import { lifecycleStateToShort } from "~/components/utils/lifecycleStateMappers";
import type { AssetRecord } from "~/stores/sync/syncAssets";
import { useWire } from "~/wire";
import { Asset } from "@storytell/cp-client";

// Format file size to human readable string
function formatFileSize(bytes: bigint | number) {
  let size = typeof bytes === "bigint" ? Number(bytes) : typeof bytes === "number" ? bytes : 0;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}

export const useScopeManagerPaneAssetsData = (search: () => string) => {
  const {
    chat: { scope },
    sync,
  } = useWire();

  // Keep a non-reactive reference to the initial scope
  const initialAssetsScope = scope.assets().reduce(
    (acc, asset) => {
      acc[asset] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  // Sorting based on initial scope to bring the in-scope assets to the top
  // but also prevent them from shifting around as the user adds/removes assets
  const allAssets = createMemo(() =>
    [...sync.assets.getAll()].sort((a, b) => {
      const aInScope = initialAssetsScope[a.id] || false;
      const bInScope = initialAssetsScope[b.id] || false;
      return Number(bInScope) - Number(aInScope);
    }),
  );

  // Filtered assets based on search
  const assets = createMemo(() => {
    const query = search();
    if (!query) return allAssets();
    return sync.assets.fuzzy
      .search(query)
      .map((result) => result.item)
      .sort((a, b) => {
        const aInScope = initialAssetsScope[a.id] || false;
        const bInScope = initialAssetsScope[b.id] || false;
        return Number(bInScope) - Number(aInScope);
      });
  });

  return {
    assets,
  };
};

export const ScopeManagerPaneAssetsTable = (props: { assets: AssetRecord[] }) => {
  const {
    chat: { scope },
  } = useWire();
  // Default sorting: by scope column, descending (in-scope first)
  const [sorting, setSorting] = createSignal<SortingState>([]);

  const allSelected = () => props.assets.every((a) => scope.assets().find((a2) => a2 === a.id));
  const someSelected = () => props.assets.some((a) => scope.assets().find((a2) => a2 === a.id));
  const toggleAll = () => {
    if (allSelected()) {
      for (const asset of props.assets) {
        scope.removeAsset(asset.id);
      }
    } else {
      for (const asset of props.assets) {
        if (asset.data.lifecycleState === Asset.AssetLifecycleState.DELETED) continue;
        scope.addAsset(asset.id);
      }
    }
  };

  // Table columns definition
  const columns: ColumnDef<AssetRecord>[] = [
    {
      id: "scope",
      size: 20,
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={allSelected()}
          indeterminate={someSelected() && !allSelected()}
          aria-label={allSelected() ? "Deselect all" : "Select all"}
          onChange={toggleAll}
          class="flex items-center justify-center"
        >
          <Checkbox.Input />
          <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
            <Show when={allSelected() || (someSelected() && !allSelected())}>
              <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
            </Show>
          </Checkbox.Control>
        </Checkbox>
      ),
      accessorFn: (row) => !!scope.assets().find((a) => a === row.id),
      cell: (info) => {
        const inScope = () => !!scope.assets().find((a) => a === info.row.original.id);
        return (
          <Checkbox
            checked={inScope()}
            aria-label={inScope() ? "Remove from scope" : "Add to scope"}
            onChange={(checked) => {
              const asset = info.row.original;
              if (checked) {
                scope.addAsset(asset.id);
              } else {
                scope.removeAsset(asset.id);
              }
            }}
            class="flex items-center justify-center"
          >
            <Checkbox.Input />
            <Checkbox.Control class="w-4 h-4 border border-primary-border dark:border-white/30 rounded bg-tile data-[checked]:bg-brand data-[checked]:border-brand flex items-center justify-center transition-colors">
              <Checkbox.Indicator as={TbCheck} class="w-4 h-4 text-on-brand stroke-3" />
            </Checkbox.Control>
          </Checkbox>
        );
      },
      footer: (props) => props.column.id,
    },
    {
      header: "Filename",
      accessorFn: (row) => row.data.originalFilename,
      cell: (info) => <span class="font-medium text-primary-text">{String(info.getValue())}</span>,
      footer: (props) => props.column.id,
    },
    // {
    //   header: "Display Name",
    //   accessorFn: (row) => row.data.displayName,
    //   cell: (info) => info.getValue(),
    //   footer: (props) => props.column.id,
    // },
    // {
    //   header: "Content Type",
    //   accessorFn: (row) => row.data.contentType.split("/").pop(),
    //   cell: (info) => info.getValue(),
    //   footer: (props) => props.column.id,
    // },
    {
      header: "File Size",
      accessorFn: (row) => row.data.fileSize,
      cell: (info) => formatFileSize(info.getValue() as number | bigint),
      footer: (props) => props.column.id,
    },
    {
      header: "Status",
      accessorFn: (row) => lifecycleStateToShort(row.data.lifecycleState),
      cell: (info) => {
        const value = () => String(info.getValue()) as ReturnType<typeof lifecycleStateToShort>;
        return (
          <span class="flex items-center gap-1 capitalize text-xs">
            <Switch>
              <Match when={value() === "uploading"}>
                <TbLoader2 class="w-3 h-3 animate-spin group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
              </Match>
              <Match when={value() === "error"}>
                <TbAlertTriangle class="w-3 h-3 text-red-700 dark:text-red-400 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform" />
              </Match>
              <Match when={value() === "processing"}>
                <div class="relative">
                  <TbFileText class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
                  <div class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
                </div>
              </Match>
              <Match when={value() === "ready"}>
                <TbCircleCheckFilled class="w-3 h-3 group-hover/btn:scale-hover group-active/btn:scale-active transition-transform text-muted-text" />
              </Match>
            </Switch>

            {value()}
          </span>
        );
      },
      footer: (props) => props.column.id,
    },
  ];

  // Create the table instance
  const table = createSolidTable({
    get data() {
      return props.assets;
    },
    columns,
    state: {
      get sorting() {
        return sorting();
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Keyboard handler for sorting
  function handleHeaderKeyDown(e: KeyboardEvent, header: Header<AssetRecord, unknown>) {
    if (header.column.getCanSort() && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      const handler = header.column.getToggleSortingHandler();
      if (handler) handler(e);
    }
  }

  return (
    <>
      <h3 class="text-muted-text text-sm mb-2">Assets</h3>
      <div class="rounded-lg border border-primary-border overflow-auto max-h-60 min-h-20 flex-auto mb-2">
        <table class="min-w-full max-w-full text-sm overflow-auto">
          <thead class="sticky top-0 bg-canvas border-b border-primary-border">
            <For each={table.getHeaderGroups()}>
              {(headerGroup) => (
                <tr>
                  <For each={headerGroup.headers}>
                    {(header) => (
                      <th
                        colSpan={header.colSpan}
                        class="text-xs px-2 py-2 font-medium text-left text-primary-text whitespace-nowrap"
                      >
                        <Show when={!header.isPlaceholder}>
                          <div
                            class={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none flex items-center gap-1"
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            role={header.column.getCanSort() ? "button" : undefined}
                            aria-pressed={header.column.getIsSorted() ? "true" : "false"}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => handleHeaderKeyDown(e as KeyboardEvent, header)}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <Show when={header.column.getIsSorted() === "asc"}>
                              <TbCaretUp class="inline-block w-4 h-4 text-brand" />
                            </Show>
                            <Show when={header.column.getIsSorted() === "desc"}>
                              <TbCaretDown class="inline-block w-4 h-4 text-brand" />
                            </Show>
                          </div>
                        </Show>
                      </th>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </thead>
          <tbody>
            <Show
              when={table.getRowModel().rows.length > 0}
              fallback={
                <tr>
                  <td colSpan={columns.length} class="text-center p-4 text-muted-text">
                    No assets found
                  </td>
                </tr>
              }
            >
              <For each={table.getRowModel().rows}>
                {(row) => (
                  <tr class="hover:bg-tile-hover transition-colors">
                    <For each={row.getVisibleCells()}>
                      {(cell) => (
                        <td class="px-3 py-2 [&:not:last-child]:border-b border-muted-border text-secondary-text">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>
    </>
  );
};
